import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Divider,
    RadioGroup,
    Radio,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    FormHelperText,
    TextField,
    Snackbar,
    Alert,
    Stack,
} from '@mui/material';
import { Input } from 'components';
import SaveIcon from '@mui/icons-material/Save';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
    CONTACT_NUMBER_FIELD_MAX_LENGTH,
    FIFTY_FIELD_MAX_LENGTH,
    LONG_TEXT_FIELD_MAX_LENGTH,
    POSTCODE_FIELD_MAX_LENGTH,
    TEXT_FIELD_MAX_LENGTH,
    TRANSIENT_ERROR_TIMEOUT_MS
} from './validationLimits';

interface Address {
    street: string;
    suburb: string;
    state: string;
    postcode: string;
    country: string;
}

interface InvoiceStatement {
    contactName: string;
    phoneNumber: string;
    emailAddressStatements: string;
    emailAddressInvoices: string;
    invoicePlatform: string;
    isPONumberMandatory: string;
    hasInvoicePlatform: string;
    needCreditAccount: string;
    poEmail: string;
}

interface GeneralInfoFormData {
    emailAddress: string;
    contactName: string;
    contactNumberType?: ContactNumberType;
    mobileNumber: string;
    eurofinsContactName: string;
    tradingName: string;
    companyName: string;
    abn: string;
    phoneNumber: string;
    businessAddress: Address;
    postalAddress: Address;
    isPostalAddressSameAsBusinessAddress: boolean;
    invoiceStatement: InvoiceStatement;
}

interface Props {
    formData: GeneralInfoFormData;
    australianStates?: any[];
    onFormDataChange: (data: Partial<GeneralInfoFormData>) => void;
    onNext: () => void;
    onSkipToReview?: () => void;
    onSave?: () => void;
    isSaving?: boolean;
    isDisabled?: boolean;
    externalErrors?: Record<string, string>;
}

type ContactNumberType = 'phoneNumber' | 'mobileNumber' | '';

const countries = [
    'Australia'
];

const getDefaultInvoiceStatement = (): InvoiceStatement => ({
    contactName: '',
    phoneNumber: '',
    emailAddressStatements: '',
    emailAddressInvoices: '',
    invoicePlatform: '',
    isPONumberMandatory: '',
    hasInvoicePlatform: '',
    needCreditAccount: '',
    poEmail: ''
});

const ABN_LENGTH = 11;
const ABN_LENGTH_ERROR = 'ABN must be 11 digits long.';
const ABN_CREDIT_ERROR = 'ABN is mandatory for a credit account';

const COMPANY_NAME_LENGTH_ERROR = 'Company Name cannot exceed 100 characters.';
const TRADING_NAME_LENGTH_ERROR = 'Trading Name cannot exceed 100 characters.';
const BUSINESS_STREET_LENGTH_ERROR = 'Business Street Name cannot exceed 100 characters.';
const BUSINESS_SUBURB_LENGTH_ERROR = 'Business Suburb cannot exceed 100 characters.';
const POSTAL_STREET_LENGTH_ERROR = 'Postal Street Name cannot exceed 100 characters.';
const POSTAL_SUBURB_LENGTH_ERROR = 'Postal Suburb cannot exceed 100 characters.';
const EUROFINS_CONTACT_LENGTH_ERROR = 'Eurofins Contact Name cannot exceed 200 characters';
const INVOICE_PLATFORM_LENGTH_ERROR = 'Invoice Platform cannot exceed 100 characters.';
const CONTACT_NAME_INVOICE_LENGTH_ERROR = 'Contact Name for Invoices cannot exceed 50 characters.';
const EMAIL_STATEMENTS_LENGTH_ERROR = 'Email for statements cannot exceed 50 characters.';
const EMAIL_INVOICES_LENGTH_ERROR = 'Email for Invoices cannot exceed 50 characters.';
const PO_EMAIL_LENGTH_ERROR = 'PO Email cannot exceed 50 characters.';
const PHONE_NUMBER_LENGTH_ERROR = 'Phone number cannot exceed 10 digits.';
const MOBILE_NUMBER_LENGTH_ERROR = 'Mobile number cannot exceed 10 digits.';
const BUSINESS_POSTCODE_LENGTH_ERROR = 'Business PostCode cannot exceed 4 characters.';
const POSTAL_POSTCODE_LENGTH_ERROR = 'Postal Code cannot exceed 4 characters.';
const CONTACT_NAME_LENGTH_ERROR = `Contact Name cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;

const getAbnValidationMessage = (abnValue: string, needCreditAccount: string) => {
    const digitsOnly = String(abnValue || '')
        .replace(/\D/g, '')
        .slice(0, 11);

    if (!digitsOnly) {
        return '';
    }

    if (digitsOnly === '0') {
        return needCreditAccount === 'yes' ? ABN_CREDIT_ERROR : '';
    }

    return digitsOnly.length === 11 ? '' : ABN_LENGTH_ERROR;
};

const getAbnDigitArray = (abnValue: string) => {
    const digits = String(abnValue || '')
        .replace(/\D/g, '')
        .slice(0, 11)
        .split('');

    return Array.from({ length: 11 }, (_, index) => digits[index] || '');
};

export const GeneralInformation: React.FC<Props> = ({
    formData,
    australianStates = [],
    onFormDataChange,
    onNext,
    onSkipToReview,
    onSave,
    isSaving,
    isDisabled,
    externalErrors = {}
}) => {
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [abnError, setAbnError] = useState(false);
    const [contactNumberType, setContactNumberType] = useState<ContactNumberType>('');
    const [abnDigits, setAbnDigits] = useState<string[]>(() => getAbnDigitArray(formData.abn));
    const abnInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const postcodeErrorTimeouts = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const transientErrorTimeouts = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

    // Update local errors when external errors change
    useEffect(() => {
        if (Object.keys(externalErrors).length > 0) {
            setErrors(externalErrors);
        }
    }, [externalErrors]);

    useEffect(() => {
        if (formData.contactNumberType && formData.contactNumberType !== contactNumberType) {
            setContactNumberType(formData.contactNumberType);
            return;
        }

        if (contactNumberType) {
            return;
        }

        if (String(formData.phoneNumber || '').trim()) {
            setContactNumberType('phoneNumber');
            return;
        }

        if (String(formData.mobileNumber || '').trim()) {
            setContactNumberType('mobileNumber');
        }
    }, [contactNumberType, formData.contactNumberType, formData.mobileNumber, formData.phoneNumber]);

    const invoiceStatement = formData.invoiceStatement || getDefaultInvoiceStatement();

    useEffect(() => {
        const validationMessage = getAbnValidationMessage(formData.abn, invoiceStatement.needCreditAccount);

        setErrors((prev) => {
            const currentAbnError = prev.abn || '';
            const isManagedAbnError = currentAbnError === ABN_LENGTH_ERROR || currentAbnError === ABN_CREDIT_ERROR;

            if (!validationMessage) {
                if (!isManagedAbnError) {
                    return prev;
                }

                const nextErrors = { ...prev };
                delete nextErrors.abn;
                return nextErrors;
            }

            if (currentAbnError === validationMessage) {
                return prev;
            }

            return {
                ...prev,
                abn: validationMessage
            };
        });
    }, [formData.abn, invoiceStatement.needCreditAccount]);

    useEffect(() => {
        const nextDigits = getAbnDigitArray(formData.abn);

        setAbnDigits((prev) => (
            prev.join('') === nextDigits.join('') ? prev : nextDigits
        ));
    }, [formData.abn]);

    useEffect(() => {
        return () => {
            Object.values(postcodeErrorTimeouts.current).forEach((timeoutId) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            });

            Object.values(transientErrorTimeouts.current).forEach((timeoutId) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            });
        };
    }, []);

    const setTransientError = (
        errorKey: string,
        errorMessage: string,
        timeoutStore: React.MutableRefObject<Record<string, ReturnType<typeof setTimeout> | null>>
    ) => {
        const existingTimeout = timeoutStore.current[errorKey];
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        setErrors(prev => ({
            ...prev,
            [errorKey]: errorMessage
        }));

        timeoutStore.current[errorKey] = setTimeout(() => {
            setErrors(prev => {
                if (prev[errorKey] !== errorMessage) {
                    return prev;
                }

                const nextErrors = { ...prev };
                delete nextErrors[errorKey];
                return nextErrors;
            });
            timeoutStore.current[errorKey] = null;
        }, TRANSIENT_ERROR_TIMEOUT_MS);
    };

    const clearTransientError = (
        errorKey: string,
        timeoutStore: React.MutableRefObject<Record<string, ReturnType<typeof setTimeout> | null>>
    ) => {
        const existingTimeout = timeoutStore.current[errorKey];
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            timeoutStore.current[errorKey] = null;
        }

        setErrors(prev => {
            if (!prev[errorKey]) {
                return prev;
            }

            const nextErrors = { ...prev };
            delete nextErrors[errorKey];
            return nextErrors;
        });
    };

    const businessAddress = formData.businessAddress || {
        street: '',
        suburb: '',
        state: '',
        postcode: '',
        country: 'Australia'
    };

    const postalAddress = formData.postalAddress || {
        street: '',
        suburb: '',
        state: '',
        postcode: '',
        country: 'Australia'
    };

    const getAustralianStateId = (value: any) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const matchedState = australianStates.find((state: any) => (
            String(state?.id) === String(value)
            || state?.name === value
        ));

        return matchedState ? String(matchedState.id) : String(value);
    };

    const getAustralianStateLabel = (state: any) => {
        return state?.name || '';
    };

    const isPostalAddressSameAsBusinessAddress = formData.isPostalAddressSameAsBusinessAddress !== false;

    const handleBusinessAddressChange = (field: string, value: string) => {
        const child = field.split('.')[1];

        const updatedBusinessAddress = {
            ...businessAddress,
            [child]: value
        };

        if (isPostalAddressSameAsBusinessAddress) {
            onFormDataChange({
                businessAddress: updatedBusinessAddress,
                postalAddress: updatedBusinessAddress
            });
        } else {
            onFormDataChange({
                businessAddress: updatedBusinessAddress
            });
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleBusinessCountryChange = (value: string) => {
        const updatedBusinessAddress = {
            ...businessAddress,
            country: value,
            state: ''
        };

        if (isPostalAddressSameAsBusinessAddress) {
            onFormDataChange({
                businessAddress: updatedBusinessAddress,
                postalAddress: updatedBusinessAddress
            });
        } else {
            onFormDataChange({
                businessAddress: updatedBusinessAddress
            });
        }
    };

    const handleInputChange = (field: string, value: any) => {

        if (field === 'companyName') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    companyName: COMPANY_NAME_LENGTH_ERROR
                }));
                return;
            }

            if (errors.companyName) {
                setErrors(prev => ({ ...prev, companyName: '' }));
            }
        }

        if (field === 'tradingName') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    tradingName: TRADING_NAME_LENGTH_ERROR
                }));
                return;
            }

            if (errors.tradingName) {
                setErrors(prev => ({ ...prev, tradingName: '' }));
            }
        }

        if (field === 'contactName') {
            if (`${value ?? ''}`.length > FIFTY_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    contactName: CONTACT_NAME_LENGTH_ERROR
                }));
                return;
            }

            if (errors.contactName) {
                setErrors(prev => ({ ...prev, contactName: '' }));
            }
        }

        if (field === 'eurofinsContactName') {
            if (`${value ?? ''}`.length > LONG_TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    eurofinsContactName: EUROFINS_CONTACT_LENGTH_ERROR
                }));
                return;
            }

            if (errors.eurofinsContactName) {
                setErrors(prev => ({ ...prev, eurofinsContactName: '' }));
            }
        }

        if (field === 'invoiceStatement.invoicePlatform') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'invoiceStatement.invoicePlatform': INVOICE_PLATFORM_LENGTH_ERROR
                }));
                return;
            }

            if (errors['invoiceStatement.invoicePlatform']) {
                setErrors(prev => ({ ...prev, 'invoiceStatement.invoicePlatform': '' }));
            }
        }

        if (field === 'invoiceStatement.contactName') {
            if (`${value ?? ''}`.length > FIFTY_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'invoiceStatement.contactName': CONTACT_NAME_INVOICE_LENGTH_ERROR
                }));
                return;
            }
            if (errors['invoiceStatement.contactName']) {
                setErrors(prev => ({ ...prev, 'invoiceStatement.contactName': '' }));
            }
        }

        if (field === 'invoiceStatement.emailAddressStatements') {
            if (`${value ?? ''}`.length > FIFTY_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'invoiceStatement.emailAddressStatements': EMAIL_STATEMENTS_LENGTH_ERROR
                }));
                return;
            }

            if (errors['invoiceStatement.emailAddressStatements']) {
                setErrors(prev => ({ ...prev, 'invoiceStatement.emailAddressStatements': '' }));
            }
        }

        if (field === 'invoiceStatement.emailAddressInvoices') {
            if (`${value ?? ''}`.length > FIFTY_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'invoiceStatement.emailAddressInvoices': EMAIL_INVOICES_LENGTH_ERROR
                }));
                return;
            }

            if (errors['invoiceStatement.emailAddressInvoices']) {
                setErrors(prev => ({ ...prev, 'invoiceStatement.emailAddressInvoices': '' }));
            }
        }

        if (field === 'invoiceStatement.poEmail') {
            if (`${value ?? ''}`.length > FIFTY_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'invoiceStatement.poEmail': PO_EMAIL_LENGTH_ERROR
                }));
                return;
            }

            if (errors['invoiceStatement.poEmail']) {
                setErrors(prev => ({ ...prev, 'invoiceStatement.poEmail': '' }));
            }
        }

        if (field === 'invoiceStatement.phoneNumber') {
            if (`${value ?? ''}`.length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
                setTransientError(field, PHONE_NUMBER_LENGTH_ERROR, transientErrorTimeouts);

                return;
            }

            if (errors['invoiceStatement.phoneNumber']) {
                clearTransientError(field, transientErrorTimeouts);
            }
        }

        if (field === 'phoneNumber') {
            if (`${value ?? ''}`.length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
                setTransientError('contactNumber', PHONE_NUMBER_LENGTH_ERROR, transientErrorTimeouts);

                return;
            }

            if (errors.contactNumber) {
                clearTransientError('contactNumber', transientErrorTimeouts);
            }
        }

        if (field === 'mobileNumber') {
            if (`${value ?? ''}`.length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
                setTransientError('contactNumber', MOBILE_NUMBER_LENGTH_ERROR, transientErrorTimeouts);

                return;
            }

            if (errors.contactNumber) {
                clearTransientError('contactNumber', transientErrorTimeouts);
            }
        }

        if (field === 'businessAddress.street') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'businessAddress.street': BUSINESS_STREET_LENGTH_ERROR
                }));
                return;
            }

            if (errors['businessAddress.street']) {
                setErrors(prev => ({ ...prev, 'businessAddress.street': '' }));
            }
        }

        if (field === 'businessAddress.suburb') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'businessAddress.suburb': BUSINESS_SUBURB_LENGTH_ERROR
                }));
                return;
            }

            if (errors['businessAddress.suburb']) {
                setErrors(prev => ({ ...prev, 'businessAddress.suburb': '' }));
            }
        }

        if (field === 'postalAddress.street') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'postalAddress.street': POSTAL_STREET_LENGTH_ERROR
                }));
                return;
            }

            if (errors['postalAddress.street']) {
                setErrors(prev => ({ ...prev, 'postalAddress.street': '' }));
            }
        }

        if (field === 'postalAddress.suburb') {
            if (`${value ?? ''}`.length > TEXT_FIELD_MAX_LENGTH) {
                setErrors(prev => ({
                    ...prev,
                    'postalAddress.suburb': POSTAL_SUBURB_LENGTH_ERROR
                }));
                return;
            }

            if (errors['postalAddress.suburb']) {
                setErrors(prev => ({ ...prev, 'postalAddress.suburb': '' }));
            }
        }

        if (field === 'businessAddress.postcode' || field === 'postalAddress.postcode') {
            const postcodeValue = `${value ?? ''}`;
            const postcodeMaxLength = POSTCODE_FIELD_MAX_LENGTH;
            const postcodeErrorMessage = field === 'businessAddress.postcode'
                ? BUSINESS_POSTCODE_LENGTH_ERROR
                : POSTAL_POSTCODE_LENGTH_ERROR;

            if (postcodeValue.length > postcodeMaxLength) {
                setTransientError(field, postcodeErrorMessage, postcodeErrorTimeouts);

                return;
            } else if (errors[field]) {
                clearTransientError(field, postcodeErrorTimeouts);
            }
        }

        if (field.startsWith('businessAddress.')) {
            handleBusinessAddressChange(field, value);
            return;
        }

        if (field.includes('.')) {
            const [parent, child] = field.split('.');

            if (parent === 'invoiceStatement') {
                onFormDataChange({
                    invoiceStatement: {
                        ...invoiceStatement,
                        [child]: value
                    }
                });
            } else if (parent === 'postalAddress') {
                onFormDataChange({
                    postalAddress: {
                        ...postalAddress,
                        [child]: value
                    }
                });
            } else {
                const parentData = formData[parent as keyof GeneralInfoFormData] as Address;
                if (parentData && typeof parentData === 'object') {
                    onFormDataChange({
                        [parent]: {
                            ...parentData,
                            [child]: value
                        }
                    });
                }
            }
        } else {
            onFormDataChange({ [field]: value });
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleCountryChange = (field: string, value: string) => {
        if (field === 'businessAddress.country') {
            handleBusinessCountryChange(value);
            return;
        }

        const [parent] = field.split('.');
        const parentData = formData[parent as keyof GeneralInfoFormData] as Address;

        if (parentData && typeof parentData === 'object') {
            onFormDataChange({
                [parent]: {
                    ...parentData,
                    country: value,
                    state: ''
                }
            });
        }
    };

    const handleContactNumberTypeChange = (value: ContactNumberType) => {
        setContactNumberType(value);

        onFormDataChange({
            contactNumberType: value,
            phoneNumber: '',
            mobileNumber: ''
        });

        if (errors.contactNumberType || errors.contactNumber) {
            setErrors(prev => ({
                ...prev,
                contactNumberType: '',
                contactNumber: ''
            }));
        }
    };

    const handleContactNumberChange = (value: string) => {
        if (contactNumberType === 'phoneNumber') {
            handleInputChange('phoneNumber', value);
            handleInputChange('mobileNumber', '');
        } else if (contactNumberType === 'mobileNumber') {
            handleInputChange('mobileNumber', value);
            handleInputChange('phoneNumber', '');
        }

        if (errors.contactNumber) {
            setErrors(prev => ({ ...prev, contactNumber: '' }));
        }
    };

    const getSelectedContactNumberValue = () => {
        if (contactNumberType === 'mobileNumber') {
            return formData.mobileNumber || '';
        }

        if (contactNumberType === 'phoneNumber') {
            return formData.phoneNumber || '';
        }

        return '';
    };

    const handlePostalSameAsBusinessChange = (isSame: boolean) => {
        if (isSame) {
            onFormDataChange({
                isPostalAddressSameAsBusinessAddress: true,
                postalAddress: { ...businessAddress }
            });
        } else {
            onFormDataChange({
                isPostalAddressSameAsBusinessAddress: false,
                postalAddress: {
                    street: '',
                    suburb: '',
                    state: '',
                    postcode: '',
                    country: 'Australia'
                }
            });
        }
    };

    const validateGeneralInfo = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.companyName?.trim()) {
            newErrors.companyName = 'Company/Business Name is required';
        } else if (formData.companyName.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.companyName = COMPANY_NAME_LENGTH_ERROR;
        }
        if (!formData.abn?.trim() && formData.invoiceStatement?.needCreditAccount !== 'no') {
            newErrors.abn = 'ABN is required';
            setAbnError(true);
        }
        if (!formData.tradingName?.trim()) {
            newErrors.tradingName = 'Trading Name is required';
        } else if (formData.tradingName.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.tradingName = TRADING_NAME_LENGTH_ERROR;
        }
        if (!formData.contactName?.trim()) {
            newErrors.contactName = 'Contact Name is required';
        }
        if (!formData.eurofinsContactName?.trim()) {
            newErrors.eurofinsContactName = 'Eurofins Contact Name is required';
        } else if (formData.eurofinsContactName.trim().length > LONG_TEXT_FIELD_MAX_LENGTH) {
            newErrors.eurofinsContactName = EUROFINS_CONTACT_LENGTH_ERROR;
        }
        if (!formData.emailAddress?.trim()) {
            newErrors.emailAddress = 'Email Address is required';
        }
        if (!formData.contactNumberType) {
            newErrors.contactNumberType = 'Contact number type is required';
        } else if (formData.contactNumberType === 'phoneNumber' && !formData.phoneNumber?.trim()) {
            newErrors.contactNumber = 'Phone Number is required';
        } else if (formData.contactNumberType === 'phoneNumber' && formData.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors.contactNumber = PHONE_NUMBER_LENGTH_ERROR;
        } else if (formData.contactNumberType === 'mobileNumber' && !formData.mobileNumber?.trim()) {
            newErrors.contactNumber = 'Mobile Number is required';
        } else if (formData.contactNumberType === 'mobileNumber' && formData.mobileNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors.contactNumber = MOBILE_NUMBER_LENGTH_ERROR;
        }
        if (!businessAddress.street?.trim()) {
            newErrors['businessAddress.street'] = 'Business Street is required';
        } else if (businessAddress.street.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.street'] = BUSINESS_STREET_LENGTH_ERROR;
        }
        if (!businessAddress.suburb?.trim()) {
            newErrors['businessAddress.suburb'] = 'Business Suburb is required';
        } else if (businessAddress.suburb.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.suburb'] = BUSINESS_SUBURB_LENGTH_ERROR;
        }
        if (!businessAddress.postcode?.trim()) {
            newErrors['businessAddress.postcode'] = 'Business Post Code is required';
        } else if (businessAddress.postcode.trim().length > POSTCODE_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.postcode'] = BUSINESS_POSTCODE_LENGTH_ERROR;
        }
        if (!businessAddress.state) {
            newErrors['businessAddress.state'] = 'Business State is required';
        }

        if (!formData.isPostalAddressSameAsBusinessAddress) {
            if (!postalAddress.street?.trim()) {
                newErrors['postalAddress.street'] = 'Postal Street is required';
            } else if (postalAddress.street.trim().length > TEXT_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.street'] = POSTAL_STREET_LENGTH_ERROR;
            }
            if (!postalAddress.suburb?.trim()) {
                newErrors['postalAddress.suburb'] = 'Postal Suburb is required';
            } else if (postalAddress.suburb.trim().length > TEXT_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.suburb'] = POSTAL_SUBURB_LENGTH_ERROR;
            }
            if (!postalAddress.postcode?.trim()) {
                newErrors['postalAddress.postcode'] = 'Postal Post Code is required';
            } else if (postalAddress.postcode.trim().length > POSTCODE_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.postcode'] = POSTAL_POSTCODE_LENGTH_ERROR;
            }
            if (!postalAddress.state) {
                newErrors['postalAddress.state'] = 'Postal State is required';
            }
        }

        if (!invoiceStatement.contactName?.trim()) {
            newErrors['invoiceStatement.contactName'] = 'Contact Name for Invoices is required';
        } else if (invoiceStatement.contactName.trim().length > FIFTY_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.contactName'] = CONTACT_NAME_INVOICE_LENGTH_ERROR;
        }
        if (invoiceStatement.phoneNumber?.trim() && invoiceStatement.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.phoneNumber'] = PHONE_NUMBER_LENGTH_ERROR;
        }
        if (!invoiceStatement.emailAddressStatements?.trim()) {
            newErrors['invoiceStatement.emailAddressStatements'] = 'Email Address for Statements is required';
        } else if (invoiceStatement.emailAddressStatements.trim().length > FIFTY_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.emailAddressStatements'] = EMAIL_STATEMENTS_LENGTH_ERROR;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.emailAddressStatements.trim())) {
            newErrors['invoiceStatement.emailAddressStatements'] = 'Please enter a valid email address';
        }

        if (!invoiceStatement.emailAddressInvoices?.trim()) {
            newErrors['invoiceStatement.emailAddressInvoices'] = 'Email Address for Invoices is required';
        } else if (invoiceStatement.emailAddressInvoices.trim().length > FIFTY_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.emailAddressInvoices'] = EMAIL_INVOICES_LENGTH_ERROR;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.emailAddressInvoices.trim())) {
            newErrors['invoiceStatement.emailAddressInvoices'] = 'Please enter a valid email address';
        }

        if (!invoiceStatement.isPONumberMandatory) {
            newErrors['invoiceStatement.isPONumberMandatory'] = 'Please select an option';
        }

        if (invoiceStatement.isPONumberMandatory === 'yes') {
            if (!invoiceStatement.poEmail?.trim()) {
                newErrors['invoiceStatement.poEmail'] = 'Email address for PO number is required';
            } else if (invoiceStatement.poEmail.trim().length > TEXT_FIELD_MAX_LENGTH) {
                newErrors['invoiceStatement.poEmail'] = PO_EMAIL_LENGTH_ERROR;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.poEmail.trim())) {
                newErrors['invoiceStatement.poEmail'] = 'Please enter a valid email address';
            }
        }
        if (!invoiceStatement.needCreditAccount) {
            newErrors['invoiceStatement.needCreditAccount'] = 'Please select an option';
        }

        const abnValidationMessage = getAbnValidationMessage(
            formData.abn,
            invoiceStatement.needCreditAccount
        );

        if (abnValidationMessage) {
            newErrors.abn = abnValidationMessage;
        }

        return newErrors;
    }

    const handleNext = () => {
        const validationErrors = validateGeneralInfo();

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        if (invoiceStatement.needCreditAccount === 'no') {
            if (onSkipToReview) {
                onSkipToReview();
            }
            else { onNext(); }
        } else {
            onNext();
        }
    };

    const formatABN = (digits: string[]) => {
        const clean = digits.join('').replace(/\D/g, '').slice(0, ABN_LENGTH);

        return clean
            .replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')
            .trim();
    };

    const updateAbnDigits = (nextDigits: string[]) => {
        setAbnDigits(nextDigits);

        const formatted = formatABN(nextDigits);

        handleInputChange('abn', formatted);
    };

    const handleAbnDigitChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);

        const updatedDigits = [...abnDigits];
        updatedDigits[index] = digit;

        updateAbnDigits(updatedDigits);

        // move focus next input
        if (digit && index < ABN_LENGTH - 1) {
            abnInputRefs.current[index + 1]?.focus();
        }
    };

    const handleAbnKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Backspace') {
            const updatedDigits = [...abnDigits];

            if (abnDigits[index]) {
                updatedDigits[index] = '';
                updateAbnDigits(updatedDigits);
            } else if (index > 0) {
                abnInputRefs.current[index - 1]?.focus();
            }
        }
    };

    const handleAbnPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        const pastedDigits = event.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, ABN_LENGTH)
            .split('');

        if (!pastedDigits.length) return;

        const nextDigits = Array.from(
            { length: ABN_LENGTH },
            (_, index) => pastedDigits[index] || ''
        );

        updateAbnDigits(nextDigits);

        const focusIndex = Math.min(pastedDigits.length, ABN_LENGTH - 1);
        abnInputRefs.current[focusIndex]?.focus();
    };

    const renderStateField = (addressType: 'businessAddress' | 'postalAddress', address: Address) => {
        const isAustralia = address.country === 'Australia';
        const fieldName = `${addressType}.state`;
        const testId = `${addressType}-state-${isAustralia ? 'select' : 'input'}`;
        const errorKey = `${addressType}.state`;

        if (isAustralia) {
            const selectedStateId = getAustralianStateId(address.state);

            return (
                <FormControl fullWidth size="small" required error={!!errors[errorKey]}>
                    <InputLabel sx={{ fontSize: '12px' }}>State</InputLabel>
                    <Select
                        value={selectedStateId}
                        label="State"
                        onChange={(e) => handleInputChange(fieldName, String(e.target.value))}
                        data-testid={testId}
                        sx={{ fontSize: '12px' }}
                    >
                        {australianStates.map((state) => (
                            <MenuItem key={state?.id} value={String(state?.id)} sx={{ fontSize: '12px' }}>
                                {getAustralianStateLabel(state)}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors[errorKey] && <FormHelperText>{errors[errorKey]}</FormHelperText>}
                </FormControl>
            );
        } else {
            return (
                <Input
                    label="State"
                    value={address.state}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Enter state or province"
                    data-testid={testId}
                    required
                    error={!!errors[errorKey]}
                    helperText={errors[errorKey]}
                />
            );
        }
    };

    const getButtonText = () => {
        if (invoiceStatement.needCreditAccount === 'no') {
            return 'Review & Confirmation';
        }
        return 'Credit Account Details';
    };

    const toTitleCase = (str = '') =>
        str
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                General Information
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Input
                        label="Name / Company Name / Business Name"
                        value={formData.companyName || ''}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        required
                        fullWidth
                        size="small"
                        error={!!errors.companyName}
                        helperText={errors.companyName}
                        data-testid="company-name-input"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Trading Name"
                        value={formData.tradingName || ''}
                        onChange={(e) => handleInputChange('tradingName', e.target.value)}
                        fullWidth
                        size="small"
                        data-testid="trading-name-input"
                        required
                        error={!!errors.tradingName}
                        helperText={errors.tradingName}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mt: 0.5, fontSize: '12px' }}>
                        ABN{' '}
                        <span style={{ color: '#666', fontStyle: 'italic', fontSize: '10px' }}>
                            (Note: ABN is 11 digits long and is required for a credit account)
                        </span>
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box data-testid="abn-acn-input">
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                flexWrap: 'nowrap'
                            }}
                        >
                            {abnDigits.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={(element) => {
                                        abnInputRefs.current[index] = element;
                                    }}
                                    value={digit}
                                    onChange={(e) =>
                                        handleAbnDigitChange(index, e.target.value)
                                    }
                                    onKeyDown={(e) =>
                                        handleAbnKeyDown(index, e)
                                    }
                                    onFocus={(e) => e.target.select()}
                                    onPaste={handleAbnPaste}
                                    size="small"
                                    error={!!errors.abn}
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: 'numeric',
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '12px',
                                            padding: '6px 0'
                                        }
                                    }}
                                    sx={{
                                        width: 32,
                                        '& .MuiOutlinedInput-root': {
                                            height: 35,
                                            borderRadius: 1
                                        },
                                        mr:
                                            index === 1 ||
                                                index === 4 ||
                                                index === 7
                                                ? 1
                                                : 0
                                    }}
                                />
                            ))}
                        </Box>

                        {errors.abn && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {errors.abn}
                            </FormHelperText>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Name of Eurofins Contact"
                        value={formData.eurofinsContactName || ''}
                        onChange={(e) => handleInputChange('eurofinsContactName', e.target.value)}
                        onBlur={(e) => handleInputChange('eurofinsContactName', toTitleCase(e.target.value))}
                        fullWidth
                        required
                        size="small"
                        data-testid="eurofins-contact-name-input"
                        error={!!errors.eurofinsContactName}
                        helperText={errors.eurofinsContactName}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Contact Name(Full Name)"
                        value={formData.contactName || ''}
                        onChange={(e) => handleInputChange('contactName', e.target.value)}
                        onBlur={(e) => handleInputChange('contactName', toTitleCase(e.target.value))}
                        fullWidth
                        size="small"
                        data-testid="contact-name-input"
                        required
                        error={!!errors.contactName}
                        helperText={errors.contactName}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Email Address"
                        value={formData.emailAddress || ''}
                        onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                        fullWidth
                        size="small"
                        data-testid="email-address-input"
                        required
                        disabled
                        error={!!errors.emailAddress}
                        helperText={errors.emailAddress}
                        InputLabelProps={{ shrink: !!formData.emailAddress }}
                        sx={{
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" required error={!!errors.contactNumberType}>
                        <InputLabel sx={{ fontSize: '12px' }}>Contact Number Type</InputLabel>
                        <Select
                            value={contactNumberType}
                            label="Contact Number Type"
                            onChange={(e) => handleContactNumberTypeChange(e.target.value as ContactNumberType)}
                            data-testid="contact-number-type-select"
                            sx={{ fontSize: '12px' }}
                        >
                            <MenuItem value="phoneNumber" sx={{ fontSize: '12px' }}>Phone Number</MenuItem>
                            <MenuItem value="mobileNumber" sx={{ fontSize: '12px' }}>Mobile Number</MenuItem>
                        </Select>
                        {errors.contactNumberType && <FormHelperText>{errors.contactNumberType}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    {contactNumberType === 'phoneNumber' && (
                        <Input
                            label="Phone Number"
                            value={getSelectedContactNumberValue()}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^[0-9]+$/.test(value)) {
                                    handleContactNumberChange(value);
                                }
                            }}
                            fullWidth
                            size="small"
                            data-testid="phone-number-input"
                            required
                            error={!!errors.contactNumber}
                            helperText={errors.contactNumber}
                        />
                    )}
                    {contactNumberType === 'mobileNumber' && (
                        <Input
                            label="Mobile Number"
                            value={getSelectedContactNumberValue()}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^[0-9]+$/.test(value)) {
                                    handleContactNumberChange(value);
                                }
                            }}
                            fullWidth
                            size="small"
                            data-testid="mobile-number-input"
                            required
                            error={!!errors.contactNumber}
                            helperText={errors.contactNumber}
                        />
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        Business Address
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Input
                        label="Number / Street Name"
                        value={businessAddress.street}
                        onChange={(e) => handleInputChange('businessAddress.street', e.target.value)}
                        onBlur={(e) => handleInputChange('businessAddress.street', toTitleCase(e.target.value))}
                        fullWidth
                        size="small"
                        error={!!errors['businessAddress.street']}
                        helperText={errors['businessAddress.street']}
                        data-testid="business-street-input"
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Suburb"
                        value={businessAddress.suburb}
                        onChange={(e) => handleInputChange('businessAddress.suburb', e.target.value)}
                        onBlur={(e) => handleInputChange('businessAddress.suburb', toTitleCase(e.target.value))}
                        fullWidth
                        size="small"
                        data-testid="business-suburb-input"
                        required
                        error={!!errors['businessAddress.suburb']}
                        helperText={errors['businessAddress.suburb']}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Post Code"
                        value={businessAddress.postcode}
                        onChange={(e) => {
                            const onlyNumbers = e.target.value.replace(/\D/g, '');
                            handleInputChange('businessAddress.postcode', onlyNumbers);
                        }}
                        fullWidth
                        size="small"
                        error={!!errors['businessAddress.postcode']}
                        helperText={errors['businessAddress.postcode']}
                        data-testid="business-postcode-input"
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" required>
                        <InputLabel sx={{ fontSize: '12px' }}>Country</InputLabel>
                        <Select
                            value={businessAddress.country}
                            label="Country"
                            onChange={(e) => handleCountryChange('businessAddress.country', e.target.value)}
                            data-testid="business-country-select"
                            sx={{ fontSize: '12px' }}
                        >
                            {countries.map((country) => (
                                <MenuItem key={country} value={country} sx={{ fontSize: '12px' }}>
                                    {country}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    {renderStateField('businessAddress', businessAddress)}
                </Grid>

                <Grid item xs={12} md={8}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 1 }}>
                        Is Billing address same as business address? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPostalAddressSameAsBusinessAddress}
                                    onChange={() => handlePostalSameAsBusinessChange(true)}
                                    color="primary"
                                    size="small"
                                    data-testid="postal-same-yes-checkbox"
                                />
                            }
                            label="Yes"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!isPostalAddressSameAsBusinessAddress}
                                    onChange={() => handlePostalSameAsBusinessChange(false)}
                                    color="primary"
                                    size="small"
                                    data-testid="postal-same-no-checkbox"
                                />
                            }
                            label="No"
                        />
                    </Box>
                </Grid>

                {/* Billing Address - shown only when "No" is selected */}
                {!isPostalAddressSameAsBusinessAddress && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                                Billing Address
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Input
                                label="Number / Street Name"
                                value={postalAddress.street}
                                onChange={(e) => handleInputChange('postalAddress.street', e.target.value)}
                                onBlur={(e) => handleInputChange('postalAddress.street', toTitleCase(e.target.value))}
                                fullWidth
                                size="small"
                                error={!!errors['postalAddress.street']}
                                helperText={errors['postalAddress.street']}
                                data-testid="postal-street-input"
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Input
                                label="Suburb"
                                value={postalAddress.suburb}
                                onChange={(e) => handleInputChange('postalAddress.suburb', e.target.value)}
                                onBlur={(e) => handleInputChange('postalAddress.suburb', toTitleCase(e.target.value))}
                                fullWidth
                                size="small"
                                data-testid="postal-suburb-input"
                                required
                                error={!!errors['postalAddress.suburb']}
                                helperText={errors['postalAddress.suburb']}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Input
                                label="Post Code"
                                value={postalAddress.postcode}
                                onChange={(e) => {
                                    const onlyNumbers = e.target.value.replace(/\D/g, '');
                                    handleInputChange('postalAddress.postcode', onlyNumbers);
                                }}
                                required
                                fullWidth
                                size="small"
                                error={!!errors['postalAddress.postcode']}
                                helperText={errors['postalAddress.postcode']}
                                data-testid="postal-postcode-input"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Country</InputLabel>
                                <Select
                                    value={postalAddress.country}
                                    label="Country"
                                    onChange={(e) => handleCountryChange('postalAddress.country', e.target.value)}
                                    data-testid="postal-country-select"
                                >
                                    {countries.map((country) => (
                                        <MenuItem key={country} value={country}>{country}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            {renderStateField('postalAddress', postalAddress)}
                        </Grid>
                    </>
                )}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                Invoice / Statement
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Input
                        label="Contact Name for Invoices"
                        value={invoiceStatement.contactName || ''}
                        onChange={(e) => handleInputChange('invoiceStatement.contactName', e.target.value)}
                        onBlur={(e) => handleInputChange('invoiceStatement.contactName', toTitleCase(e.target.value))}
                        fullWidth
                        size="small"
                        error={!!errors['invoiceStatement.contactName']}
                        helperText={errors['invoiceStatement.contactName']}
                        data-testid="contact-name-invoices-input"
                        required
                        InputLabelProps={{ shrink: !!invoiceStatement.contactName }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Phone Number"
                        value={invoiceStatement.phoneNumber || ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[0-9]+$/.test(value)) {
                                handleInputChange('invoiceStatement.phoneNumber', value);
                            }
                        }}
                        fullWidth
                        size="small"
                        data-testid="phone-number-invoices-input"
                        error={!!errors['invoiceStatement.phoneNumber']}
                        helperText={errors['invoiceStatement.phoneNumber']}
                        InputLabelProps={{ shrink: !!invoiceStatement.phoneNumber }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Email Address for Statements"
                        type="email"
                        value={invoiceStatement.emailAddressStatements || ''}
                        onChange={(e) => handleInputChange('invoiceStatement.emailAddressStatements', e.target.value)}
                        fullWidth
                        size="small"
                        error={!!errors['invoiceStatement.emailAddressStatements']}
                        helperText={errors['invoiceStatement.emailAddressStatements']}
                        data-testid="email-statements-input"
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Email Address for Invoices"
                        type="email"
                        value={invoiceStatement.emailAddressInvoices || ''}
                        onChange={(e) => handleInputChange('invoiceStatement.emailAddressInvoices', e.target.value)}
                        fullWidth
                        size="small"
                        error={!!errors['invoiceStatement.emailAddressInvoices']}
                        helperText={errors['invoiceStatement.emailAddressInvoices']}
                        data-testid="email-invoices-input"
                        required
                        InputLabelProps={{ shrink: !!invoiceStatement.emailAddressInvoices }}
                    />
                </Grid>

                <Grid item xs={12} md={8}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 1, fontSize: '12px' }}>
                        Is PO number mandatory for invoicing? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" required error={!!errors['invoiceStatement.isPONumberMandatory']}>
                        <RadioGroup
                            value={invoiceStatement.isPONumberMandatory || ''}
                            onChange={(e) => handleInputChange('invoiceStatement.isPONumberMandatory', e.target.value)}
                            row
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio size="small" />}
                                label="Yes"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio size="small" />}
                                label="No"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                        </RadioGroup>
                        {errors['invoiceStatement.isPONumberMandatory'] && <FormHelperText>{errors['invoiceStatement.isPONumberMandatory']}</FormHelperText>}
                    </FormControl>
                </Grid>

                {invoiceStatement.isPONumberMandatory === 'yes' && (
                    <Grid item xs={12}>
                        <Input
                            label="Email address for PO number"
                            value={invoiceStatement.poEmail || ''}
                            onChange={(e) => handleInputChange('invoiceStatement.poEmail', e.target.value)}
                            fullWidth
                            size="small"
                            data-testid="po-email-input"
                            required
                            error={!!errors['invoiceStatement.poEmail']}
                            helperText={errors['invoiceStatement.poEmail']}
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={8}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 1, fontSize: '12px' }}>
                        Does your company/corporation use an invoice platform for submitting & delivery of invoices?
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={invoiceStatement.hasInvoicePlatform || ''}
                            onChange={(e) => handleInputChange('invoiceStatement.hasInvoicePlatform', e.target.value)}
                            row
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio size="small" />}
                                label="Yes"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio size="small" />}
                                label="No"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {invoiceStatement.hasInvoicePlatform === 'yes' && (
                    <Grid item xs={12}>
                        <Input
                            label="Name of Invoice Platform"
                            value={invoiceStatement.invoicePlatform || ''}
                            onChange={(e) => handleInputChange('invoiceStatement.invoicePlatform', e.target.value)}
                            fullWidth
                            size="small"
                            data-testid="invoice-platform-input"
                            error={!!errors['invoiceStatement.invoicePlatform']}
                            helperText={errors['invoiceStatement.invoicePlatform']}
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={8}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 1, fontSize: '12px' }}>
                        Do you wish to apply for a 30-day credit account? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" required error={!!errors['invoiceStatement.needCreditAccount']}>
                        <RadioGroup
                            value={invoiceStatement.needCreditAccount || ''}
                            onChange={(e) => handleInputChange('invoiceStatement.needCreditAccount', e.target.value)}
                            row
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio size="small" />}
                                label="Yes"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio size="small" />}
                                label="No"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                        </RadioGroup>
                        {errors['invoiceStatement.needCreditAccount'] && <FormHelperText>{errors['invoiceStatement.needCreditAccount']}</FormHelperText>}
                    </FormControl>
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                    flexDirection: {
                        xs: 'column',
                        sm: 'row',
                    },
                    gap: 2,
                }}
            >
                <Box />

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: {
                            xs: 'column',
                            sm: 'row',
                        },
                        width: {
                            xs: '100%',
                            sm: 'auto',
                        },
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onSave}
                        disabled={isSaving || isDisabled}
                        startIcon={<SaveIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto',
                            },
                        }}
                        data-testid="save-button"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={isDisabled}
                        startIcon={<CreditCardIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto',
                            },
                        }}
                        data-testid="next-step-button"
                    >
                        {getButtonText()}
                    </Button>
                </Box>
            </Box>
            <Stack>
                <Snackbar
                    open={abnError}
                    autoHideDuration={6000}
                    onClose={() => setAbnError(false)}
                >
                    <Alert
                        onClose={() => setAbnError(false)}
                        severity="error"
                        color="error"
                    >
                        ABN is required
                    </Alert>
                </Snackbar>
            </Stack>
        </Box>
    );
};
